import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription, clearSubscriptionCache } from "@/hooks/useSubscription";

// Types
interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: ReturnType<typeof useProfile>['profile'];
  subscription: ReturnType<typeof useSubscription>['subscription'];
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  forceLogout: () => void;
  isAdmin: boolean;
  isPremium: boolean; // Always true now
  checkSubscription: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  subscription: null,
  isLoading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  forceLogout: () => {},
  isAdmin: false,
  isPremium: true, // Always true now
  checkSubscription: async () => {},
  refreshSubscriptionStatus: async () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

import { checkRateLimit } from "@/utils/rateLimiter";

// Helper function to clear all auth related data from localStorage
const clearAllAuthData = () => {
  // Clear the subscription cache
  clearSubscriptionCache();
  
  // Clear Supabase session data from localStorage
  try {
    localStorage.removeItem('supabase.auth.token');
    
    // Find and remove any other Supabase related items
    const itemsToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('supabase') || key.includes('sb-'))) {
        itemsToRemove.push(key);
      }
    }
    
    // Remove items in a separate loop to avoid issues with changing localStorage during iteration
    itemsToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    // Silent fail in production
  }
};


// Auth Provider component
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  
  // Use our custom hooks
  const { profile, isAdmin } = useProfile(user);
  const { 
    subscription, 
    isPremium, 
    refreshSubscriptionStatus 
  } = useSubscription(user?.id);

  // Set up auth state change listener and initial session
  useEffect(() => {
    let mounted = true;
    
    const setupAuth = async () => {
      try {
        // Set up auth state listener first
        const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (!mounted) return;
          
          //console.log(`Auth state changed: ${event}`);
          
          setSession(newSession);
          setUser(newSession?.user || null);
          
          // Only refresh subscription on sign in event to reduce API calls
          if (event === "SIGNED_IN") {
            //console.log("Auth state changed to SIGNED_IN, refreshing subscription");
            // Use setTimeout to prevent potential auth deadlock
            setTimeout(() => {
              refreshSubscriptionStatus(true);
            }, 500);
          } else if (event === "SIGNED_OUT") {
            //console.log("Auth state changed to SIGNED_OUT, clearing cache");
            clearAllAuthData();
          }
        });
        
        // Check for existing session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (mounted) {
          //console.log("Initial auth check:", initialSession ? "Session found" : "No session");
          setSession(initialSession);
          setUser(initialSession?.user || null);
          
          // Initial subscription check for returning users only if they have a session
          if (initialSession?.user) {
            //console.log("Initial session found, refreshing subscription");
            // Use setTimeout to prevent potential auth deadlock
            setTimeout(() => {
              refreshSubscriptionStatus(true);
            }, 500);
          }
        }
        
        if (mounted) setIsLoading(false);
        
        return () => {
          mounted = false;
          data.subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error in auth setup:", error);
        if (mounted) setIsLoading(false);
      }
    };
    
    setupAuth();
    
    return () => {
      mounted = false;
    };
  }, [refreshSubscriptionStatus]);

  // Force logout function - for emergency use when normal logout fails
  const forceLogout = useCallback(() => {
    console.log("Force logout initiated");
    
    // Clear all auth data
    clearAllAuthData();
    
    // Reset state
    setUser(null);
    setSession(null);
    
    // Navigate to home
    navigate("/");
    
    toast({
      title: "Sessão encerrada",
      description: "Você foi desconectado da sua conta.",
    });
  }, [navigate]);

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      const { allowed, retryAfterMs } = checkRateLimit(`login_${email.toLowerCase().trim()}`, 5, 60_000);
      if (!allowed) {
        toast({
          title: "Muitas tentativas de login",
          description: `Por segurança, aguarde ${Math.ceil(retryAfterMs / 1000)} segundos antes de tentar novamente.`,
          variant: "destructive",
        });
        return;
      }

      setIsLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) throw error;
      
      toast({
        title: "Login bem-sucedido",
        description: "Você foi autenticado com sucesso.",
      });
      
      // No need to call refreshSubscriptionStatus here as the auth state change event will trigger it
      if (data.user) {
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: "Erro ao fazer login",
        description: error.message || "Ocorreu um erro durante o login. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Sign up function
  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Registro bem-sucedido",
        description: "Sua conta foi criada. Verifique seu email para confirmar o registro.",
      });
      
      navigate("/auth/login");
    } catch (error: any) {
      toast({
        title: "Erro ao registrar",
        description: error.message || "Ocorreu um erro durante o registro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced sign out function with improved reliability
  const signOut = async () => {
    console.log("Sign out initiated");
    try {
      setIsLoading(true);
      
      // Clear all authentication data first
      clearAllAuthData();
      
      // Set up fallback timer for 2 seconds
      const fallbackTimer = setTimeout(() => {
        console.log("Sign out fallback timer triggered");
        forceLogout();
      }, 2000);
      
      // Attempt Supabase signOut
      const { error } = await supabase.auth.signOut();
      
      // Clear the fallback timer if successful
      clearTimeout(fallbackTimer);
      
      if (error) {
        console.warn("Supabase sign out error:", error);
        // Even with error, force clean state on client side
        forceLogout();
        return;
      }
      
      console.log("Supabase sign out successful");
      
      // Double check auth state after signOut
      const { data: sessionCheck } = await supabase.auth.getSession();
      if (sessionCheck.session) {
        console.warn("Session still exists after signOut, forcing logout");
        forceLogout();
        return;
      }
      
      // Set local state to logged out
      setUser(null);
      setSession(null);
      
      toast({
        title: "Logout bem-sucedido",
        description: "Você saiu da sua conta.",
      });
      
      navigate("/");
    } catch (error: any) {
      console.error("Sign out error:", error);
      
      // Force logout in case of any error
      forceLogout();
    } finally {
      setIsLoading(false);
    }
  };

  // Simplified checkSubscription - no longer needed but kept for compatibility
  const checkSubscription = useCallback(async () => {
    // All users have premium access now
    return;
  }, []);

  // Provide the auth context to the app
  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        subscription,
        isLoading,
        signIn,
        signUp,
        signOut,
        forceLogout,
        isAdmin,
        isPremium: true, // Always true now
        checkSubscription,
        refreshSubscriptionStatus: () => Promise.resolve(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
